<template>
  <transition name="tlp-fade">
    <div
      v-if="activePopup"
      class="tlp-overlay"
      role="presentation"
      @mousedown.self="closePopup"
    >
      <section
        ref="dialog"
        class="tlp-dialog"
        :class="[
          `tlp-dialog--${activePopup.layout}`,
          { 'tlp-dialog--success': success }
        ]"
        :style="themeStyles"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`tlp-title-${activePopup.id}`"
        @mousedown.stop
      >
        <button
          type="button"
          class="tlp-close"
          aria-label="Закрыть окно"
          :disabled="loading"
          @click="closePopup"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <div class="tlp-photo">
          <img :src="activePopup.image" :alt="activePopup.imageAlt" loading="eager">
          <span class="tlp-photo__veil" aria-hidden="true"></span>
          <div class="tlp-photo__label">
            <span>{{ activePopup.photoLabel }}</span>
            <strong>{{ activePopup.photoNote }}</strong>
          </div>
        </div>

        <div class="tlp-panel">
          <template v-if="!success">
            <div class="tlp-copy">
              <span class="tlp-kicker">{{ activePopup.kicker }}</span>
              <h2 :id="`tlp-title-${activePopup.id}`">{{ activePopup.title }}</h2>
              <p>{{ activePopup.text }}</p>
            </div>

            <form class="tlp-form" novalidate @focusin="trackFormStart" @submit.prevent="submit">
              <div class="tlp-form__row">
                <label>
                  <span>Ваше имя</span>
                  <input
                    ref="firstInput"
                    v-model.trim="form.name"
                    type="text"
                    autocomplete="name"
                    maxlength="80"
                    placeholder="Как к вам обращаться"
                  >
                </label>

                <label>
                  <span>Телефон</span>
                  <input
                    v-model.trim="form.phone"
                    v-phone-mask
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel"
                    maxlength="18"
                    placeholder="+7 (999) 000-00-00"
                    required
                  >
                </label>
              </div>

              <label>
                <span>Электронная почта</span>
                <input
                  v-model.trim="form.email"
                  type="email"
                  autocomplete="email"
                  maxlength="120"
                  placeholder="Необязательно"
                >
              </label>

              <label class="tlp-consent">
                <input v-model="form.consent" type="checkbox" required>
                <span>Даю отдельное <button type="button" @click="openLegal('/personal-data-consent')">согласие на обработку персональных данных</button>. <button type="button" @click="openLegal('/privacy')">Политика</button>.</span>
              </label>
              <label class="tlp-consent">
                <input v-model="form.marketing" type="checkbox">
                <span>Хочу получать акции и предложения. <button type="button" @click="openLegal('/marketing-consent')">Согласие на рекламу</button> — необязательно.</span>
              </label>

              <p v-if="error" class="tlp-message tlp-message--error">{{ error }}</p>

              <button class="tlp-submit" type="submit" :disabled="loading">
                <span>{{ loading ? 'Отправляем…' : activePopup.button }}</span>
                <svg v-if="!loading" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M4 10h11M11 5l5 5-5 5" />
                </svg>
              </button>

              <small class="tlp-note">{{ activePopup.note }}</small>
            </form>
          </template>

          <div v-else class="tlp-success" role="status">
            <span class="tlp-success__icon">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 7" /></svg>
            </span>
            <span class="tlp-kicker">Заявка принята</span>
            <h2>Спасибо! Мы скоро свяжемся с вами</h2>
            <p>Окно закроется автоматически. Другие предложения больше показываться не будут.</p>
          </div>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { trackGoal, trackError } from '@/analytics/metrica'

const API_URL = 'https://sitebypro-server.ru/druzbamebel'
const STORAGE_KEY = 'druzhba_timed_lead_popups_v2'
const LEGACY_STORAGE_KEY = 'druzhba_timed_lead_popups_v1'
const DISPLAY_DELAY = 50000
const DEFER_WHEN_BUSY = 10000

const POPUPS = [
  {
    id: 'consultation',
    kicker: 'Бесплатный подбор',
    title: 'Подберём диван под вашу комнату и образ жизни',
    text: 'Расскажите, кто будет пользоваться диваном и что для вас важно. Специалист предложит несколько подходящих вариантов без долгого поиска.',
    button: 'Получить подборку',
    note: 'Бесплатно. Без навязчивых звонков и обязательств.',
    photoLabel: 'Помощь специалиста',
    photoNote: 'Подбор по размеру, бюджету и ежедневным задачам',
    image: '/images/lead-popups-v2/01-consultation.webp',
    imageAlt: 'Покупатели выбирают ткань и диван с консультантом',
    accent: '#b07c50',
    accentDark: '#8f603b',
    soft: '#f5ede6',
    ink: '#2b241f',
    layout: 'split'
  }
]

function defaultState() {
  return {
    version: 2,
    initializedAt: Date.now(),
    viewed: [],
    nextAt: null,
    completed: false,
    submitted: false,
    lastEvent: 'initialized',
    lastPath: window.location.pathname
  }
}

export default {
  name: 'TimedLeadPopups',
  data() {
    return {
      activePopup: null,
      form: {
        name: '',
        phone: '',
        email: '',
        consent: false,
        marketing: false
      },
      state: defaultState(),
      timer: null,
      successTimer: null,
      loading: false,
      success: false,
      error: '',
      previousOverflow: '',
      formStartedFor: ''
    }
  },
  computed: {
    isEligibleRoute() {
      const path = String(this.$route && this.$route.path ? this.$route.path : '/')
      return !/^\/cart(?:\/|$)/.test(path) && !/^\/payment(?:\/|$)/.test(path)
    },
    themeStyles() {
      if (!this.activePopup) return {}
      return {
        '--tlp-accent': this.activePopup.accent,
        '--tlp-accent-dark': this.activePopup.accentDark,
        '--tlp-soft': this.activePopup.soft,
        '--tlp-ink': this.activePopup.ink
      }
    }
  },
  watch: {
    '$route.fullPath'() {
      this.handleRouteChange()
    }
  },
  created() {
    this.loadState()
    this.exposeDiagnosticStatus()
  },
  mounted() {
    this.form.email = String(window.localStorage.getItem('email') || '').trim()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('druzhba-lead-sent', this.handleExternalLeadSent)
    this.handleRouteChange()
    this.exposeDiagnosticStatus()
  },
  beforeDestroy() {
    this.clearTimers()
    this.restoreBody()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('druzhba-lead-sent', this.handleExternalLeadSent)
  },
  methods: {
    exposeDiagnosticStatus() {
      window.__DRUZHBA_TIMED_POPUPS__ = {
        mounted: true,
        storageKey: STORAGE_KEY,
        popupCount: POPUPS.length,
        path: window.location.pathname
      }
    },
    loadState() {
      let saved = null

      try {
        const currentRaw = window.localStorage.getItem(STORAGE_KEY)
        const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY)
        saved = JSON.parse(currentRaw || legacyRaw || 'null')
      } catch (error) {
        console.error('[TIMED_POPUPS] Не удалось прочитать localStorage', error)
      }

      if (!saved || !Array.isArray(saved.viewed)) {
        this.state = defaultState()
        this.saveState('initialized')
        return
      }

      this.state = {
        version: 2,
        initializedAt: Number(saved.initializedAt) || Date.now(),
        viewed: saved.viewed.filter(id => POPUPS.some(item => item.id === id)),
        nextAt: Number(saved.nextAt) || null,
        completed: Boolean(saved.completed),
        submitted: Boolean(saved.submitted),
        lastEvent: String(saved.lastEvent || 'restored'),
        lastPath: String(saved.lastPath || window.location.pathname)
      }

      if (this.state.viewed.length >= POPUPS.length) {
        this.state.completed = true
        this.state.nextAt = null
      }

      this.saveState('restored')
    },
    saveState(eventName) {
      this.state.lastEvent = eventName || this.state.lastEvent
      this.state.lastPath = window.location.pathname

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state))
      } catch (error) {
        console.error('[TIMED_POPUPS] localStorage недоступен', error)
      }

      this.exposeDiagnosticStatus()
    },
    handleRouteChange() {
      if (!this.isEligibleRoute) {
        this.clearSchedule()
        if (this.activePopup) this.hidePopup(false)
        this.state.nextAt = null
        this.saveState('paused-on-excluded-route')
        return
      }

      if (!this.activePopup && !this.state.completed && !this.state.submitted) {
        this.scheduleNext(false)
      }
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.clearSchedule()
        this.saveState('paused-hidden')
        return
      }

      if (this.isEligibleRoute && !this.activePopup && !this.state.completed && !this.state.submitted) {
        this.scheduleNext(false)
      }
    },
    handleExternalLeadSent(event) {
      if (event && event.detail && event.detail.placement === 'timed-popup') return
      this.clearSchedule()
      this.state.submitted = true
      this.state.completed = true
      this.state.nextAt = null
      this.saveState('lead-sent-elsewhere')
      if (this.activePopup) this.hidePopup(false)
    },
    scheduleNext(forceFresh) {
      this.clearSchedule()

      if (
        !this.isEligibleRoute ||
        document.hidden ||
        this.activePopup ||
        this.state.completed ||
        this.state.submitted
      ) return

      const unseen = POPUPS.filter(item => !this.state.viewed.includes(item.id))
      if (!unseen.length) {
        this.state.completed = true
        this.state.nextAt = null
        this.saveState('all-viewed')
        return
      }

      const now = Date.now()
      if (forceFresh || !this.state.nextAt || this.state.nextAt <= now) {
        this.state.nextAt = now + DISPLAY_DELAY
      }

      this.saveState('scheduled')
      const wait = Math.max(0, this.state.nextAt - now)
      this.timer = window.setTimeout(this.tryOpenNext, wait)
    },
    tryOpenNext() {
      this.clearSchedule()

      if (!this.isEligibleRoute || document.hidden || this.state.completed || this.state.submitted) return

      if (document.body.style.overflow === 'hidden') {
        this.state.nextAt = Date.now() + DEFER_WHEN_BUSY
        this.saveState('deferred-busy')
        this.scheduleNext(false)
        return
      }

      const next = POPUPS.find(item => !this.state.viewed.includes(item.id))
      if (!next) {
        this.state.completed = true
        this.state.nextAt = null
        this.saveState('all-viewed')
        return
      }

      this.activePopup = next
      this.formStartedFor = ''
      trackGoal(this.popupGoal('show', next), { popup: { id: next.id, title: next.title, position: this.state.viewed.length + 1 } })
      this.success = false
      this.error = ''
      this.loading = false
      this.state.viewed.push(next.id)
      this.state.nextAt = null
      if (this.state.viewed.length >= POPUPS.length) this.state.completed = true
      this.saveState(`shown-${next.id}`)

      this.previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.handleKeydown)

      this.$nextTick(() => {
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 720px)').matches
        if (isMobile) {
          if (this.$refs.dialog) this.$refs.dialog.scrollTop = 0
          return
        }
        if (this.$refs.firstInput) this.$refs.firstInput.focus()
      })
    },
    closePopup() {
      if (this.loading) return
      if (this.activePopup && !this.success) trackGoal(this.popupGoal('close'), { popup: { id: this.activePopup.id } })
      this.hidePopup(true)
    },
    hidePopup(schedule) {
      window.clearTimeout(this.successTimer)
      this.successTimer = null
      this.activePopup = null
      this.success = false
      this.error = ''
      this.loading = false
      this.restoreBody()
      document.removeEventListener('keydown', this.handleKeydown)

      if (schedule && !this.state.completed && !this.state.submitted && this.isEligibleRoute) {
        this.state.nextAt = Date.now() + DISPLAY_DELAY
        this.saveState('closed-and-rescheduled')
        this.scheduleNext(false)
      }
    },
    restoreBody() {
      document.body.style.overflow = this.previousOverflow || ''
      this.previousOverflow = ''
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.closePopup()
    },
    clearSchedule() {
      window.clearTimeout(this.timer)
      this.timer = null
    },
    clearTimers() {
      this.clearSchedule()
      window.clearTimeout(this.successTimer)
      this.successTimer = null
    },
    openLegal(path) {
      const routeData = this.$router.resolve(path)
      window.open(routeData.href, '_blank', 'noopener,noreferrer')
    },
    validate() {
      if (this.form.name && this.form.name.length < 2) return 'Проверьте имя.'
      if (this.form.phone.replace(/\D/g, '').length < 11) return 'Проверьте номер телефона.'
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) return 'Проверьте электронную почту.'
      if (!this.form.consent) return 'Подтвердите согласие на обработку данных.'
      return ''
    },
    popupGoal(action, popup = this.activePopup) {
      const index = POPUPS.findIndex(item => popup && item.id === popup.id) + 1
      return `tp${Math.max(index, 1)}_${action}`
    },
    trackFormStart() {
      if (!this.activePopup || this.formStartedFor === this.activePopup.id) return
      this.formStartedFor = this.activePopup.id
      trackGoal(this.popupGoal('start'), { popup: { id: this.activePopup.id } })
    },
    async submit() {
      if (this.loading || this.success || !this.activePopup) return

      this.error = this.validate()
      if (this.error) return

      this.loading = true
      const current = this.activePopup
      trackGoal(this.popupGoal('submit', current), { popup: { id: current.id } })
      const source = `${window.location.origin}${this.$route.fullPath}`
      const details = [
        `Имя: ${this.form.name}`,
        `Предложение: ${current.title}`,
        `Вариант окна: ${current.id}`,
        `Страница обращения: ${source}`
      ].join('\n')

      try {
        const response = await axios.post(
          `${API_URL}/user/work-reguest`,
          {
            type: {
              name: `timed-popup-${current.id}`,
              title: `Заявка из модального окна: ${current.title}`
            },
            tail: {
              email: this.form.email,
              phone: this.form.phone,
              text: details
            },
            legal: {
              personal_data: { accepted: true, document_version: '2026-09-01-v1' },
              marketing: { accepted: this.form.marketing === true, document_version: '2026-09-01-v1' }
            }
          },
          { timeout: 20000 }
        )

        const responseStatus = String(response && response.data ? response.data.status : '')
        if (responseStatus !== '200' && responseStatus !== '0') {
          const message = response && response.data && response.data.data
            ? response.data.data.text
            : ''
          throw new Error(message || 'Не удалось отправить заявку.')
        }

        this.success = true
        trackGoal(this.popupGoal('success', current), { popup: { id: current.id } })
        this.state.submitted = true
        this.state.completed = true
        this.state.nextAt = null
        this.saveState('submitted')

        window.dispatchEvent(new CustomEvent('druzhba-lead-sent', {
          detail: {
            placement: 'timed-popup',
            popup: current.id,
            path: this.$route.path
          }
        }))

        this.successTimer = window.setTimeout(() => this.hidePopup(false), 3000)
      } catch (error) {
        const responseText = error && error.response && error.response.data && error.response.data.data
          ? error.response.data.data.text
          : ''
        this.error = responseText || error.message || 'Не удалось отправить заявку. Позвоните нам по номеру +7 964 888-99-05.'
        trackError(`timed-popup:${current.id}`, error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.tlp-overlay{position:fixed;z-index:1400;inset:0;display:grid;place-items:center;padding:24px;background:rgba(31,25,21,.68);backdrop-filter:blur(7px);text-align:left}.tlp-dialog{--tlp-accent:#b07c50;--tlp-accent-dark:#8f603b;--tlp-soft:#f2e7dc;--tlp-ink:#2b241f;position:relative;display:grid;width:min(1010px,100%);max-height:calc(100dvh - 48px);grid-template-columns:minmax(340px,.95fr) minmax(420px,1.05fr);overflow:auto;border:0;border-radius:24px;background:#fff;box-shadow:0 32px 110px rgba(24,18,14,.36);color:var(--tlp-ink)}.tlp-dialog--reverse .tlp-photo{order:2}.tlp-dialog--reverse .tlp-panel{order:1}.tlp-dialog--editorial{grid-template-columns:minmax(370px,1.02fr) minmax(410px,.98fr)}.tlp-dialog--dark .tlp-panel{background:linear-gradient(145deg,var(--tlp-accent-dark),#263532);color:#fff}.tlp-dialog--dark .tlp-copy p,.tlp-dialog--dark .tlp-form label>span,.tlp-dialog--dark .tlp-consent,.tlp-dialog--dark .tlp-note{color:rgba(255,255,255,.75)}.tlp-dialog--dark .tlp-kicker{color:#d9ebe6}.tlp-close{position:absolute;z-index:5;top:17px;right:17px;display:grid;width:40px;height:40px;place-items:center;padding:0;border:1px solid rgba(255,255,255,.72);border-radius:50%;background:rgba(255,255,255,.9);color:#4d423a;box-shadow:0 7px 22px rgba(42,31,23,.12);cursor:pointer}.tlp-dialog--reverse .tlp-close{right:17px;left:auto}.tlp-close:hover{border-color:var(--tlp-accent);color:var(--tlp-accent-dark);transform:rotate(4deg)}.tlp-close:disabled{opacity:.5;cursor:wait}.tlp-close svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round}.tlp-photo{position:relative;min-height:600px;overflow:hidden;background:var(--tlp-soft)}.tlp-photo img{display:block;width:100%;height:100%;min-height:600px;object-fit:cover}.tlp-photo__veil{position:absolute;inset:0;background:linear-gradient(180deg,transparent 46%,rgba(28,21,17,.7) 100%)}.tlp-photo__label{position:absolute;right:28px;bottom:27px;left:28px;color:#fff}.tlp-photo__label span{display:block;margin-bottom:6px;font-size:10px;font-weight:800;letter-spacing:.09em;text-transform:uppercase}.tlp-photo__label strong{display:block;max-width:360px;font-size:16px;font-weight:500;line-height:1.38}.tlp-panel{display:flex;min-height:600px;flex-direction:column;justify-content:center;padding:52px 50px 42px;background:linear-gradient(150deg,#fff 0%,var(--tlp-soft) 150%)}.tlp-copy{max-width:500px}.tlp-kicker{display:inline-flex;align-items:center;min-height:27px;padding:0 11px;border:1px solid rgba(176,124,80,.3);border-radius:999px;background:rgba(176,124,80,.09);color:var(--tlp-accent-dark);font-size:9px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.tlp-copy h2,.tlp-success h2{margin:15px 0 0;font-size:40px;font-weight:500;line-height:1.05;letter-spacing:-1.6px}.tlp-copy p,.tlp-success p{margin:17px 0 0;color:#70645c;font-size:13px;line-height:1.65}.tlp-form{display:grid;gap:14px;margin-top:26px}.tlp-form__row{display:grid;grid-template-columns:1fr 1fr;gap:11px}.tlp-form label{display:grid;gap:7px}.tlp-form label>span{color:#675c54;font-size:9px;font-weight:700}.tlp-form input{width:100%;height:47px;padding:0 14px;border:1px solid #ded2c8;border-radius:10px;outline:0;background:#fff;color:#2b241f;font:inherit;font-size:12px}.tlp-form input:focus{border-color:var(--tlp-accent);box-shadow:0 0 0 3px rgba(176,124,80,.13)}.tlp-consent{display:flex!important;align-items:flex-start;gap:9px!important;color:#7b6f67;font-size:9px;line-height:1.5}.tlp-consent input{width:16px;height:16px;flex:0 0 auto;margin:0;accent-color:var(--tlp-accent)}.tlp-consent button{padding:0;border:0;background:transparent;color:var(--tlp-accent-dark);font:inherit;text-decoration:underline;cursor:pointer}.tlp-message{margin:0;padding:10px 12px;border-radius:9px;font-size:10px;line-height:1.45}.tlp-message--error{border:1px solid #ead3ca;background:#fff6f2;color:#9d3f32}.tlp-submit{display:flex;width:100%;height:49px;align-items:center;justify-content:center;gap:10px;border:0;border-radius:10px;background:var(--tlp-accent);color:#fff;font-size:10px;font-weight:800;letter-spacing:.02em;cursor:pointer}.tlp-submit:hover{background:var(--tlp-accent-dark);transform:translateY(-1px)}.tlp-submit:disabled{opacity:.65;cursor:wait;transform:none}.tlp-submit svg{width:17px;height:17px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}.tlp-note{color:#92867e;font-size:8px;text-align:center}.tlp-success{display:flex;flex-direction:column;align-items:flex-start;justify-content:center}.tlp-success__icon{display:grid;width:56px;height:56px;place-items:center;margin-bottom:23px;border-radius:50%;background:var(--tlp-accent);color:#fff}.tlp-success__icon svg{width:28px;height:28px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}.tlp-fade-enter-active,.tlp-fade-leave-active{transition:opacity .2s ease}.tlp-fade-enter-active .tlp-dialog,.tlp-fade-leave-active .tlp-dialog{transition:transform .25s ease,opacity .2s ease}.tlp-fade-enter,.tlp-fade-leave-to{opacity:0}.tlp-fade-enter .tlp-dialog,.tlp-fade-leave-to .tlp-dialog{opacity:0;transform:translateY(16px) scale(.985)}
@media(max-width:900px){.tlp-dialog,.tlp-dialog--editorial{grid-template-columns:minmax(280px,.82fr) minmax(400px,1.18fr)}.tlp-panel{padding:45px 35px 36px}.tlp-copy h2,.tlp-success h2{font-size:34px}.tlp-photo,.tlp-photo img,.tlp-panel{min-height:565px}}
@media(max-width:720px){.tlp-overlay{align-items:end;padding:0}.tlp-dialog,.tlp-dialog--editorial{width:100%;max-height:94dvh;grid-template-columns:1fr;border-radius:22px 22px 0 0}.tlp-dialog--reverse .tlp-photo,.tlp-dialog--reverse .tlp-panel{order:initial}.tlp-dialog--reverse .tlp-close{right:12px;left:auto}.tlp-photo{display:block!important;width:100%;height:190px;min-height:190px;flex:0 0 190px;overflow:hidden;visibility:visible;opacity:1}.tlp-photo img{position:absolute;inset:0;display:block!important;width:100%;height:100%;min-height:0;object-fit:cover;visibility:visible;opacity:1}.tlp-photo__label{right:20px;bottom:17px;left:20px}.tlp-photo__label span{margin-bottom:4px;font-size:8px}.tlp-photo__label strong{font-size:13px}.tlp-panel{min-height:0;padding:27px 20px 23px}.tlp-close{top:12px;right:12px;width:36px;height:36px}.tlp-copy h2,.tlp-success h2{max-width:520px;margin-top:12px;font-size:29px;line-height:1.08;letter-spacing:-1px}.tlp-copy p,.tlp-success p{margin-top:12px;font-size:11px}.tlp-form{gap:12px;margin-top:20px}.tlp-form__row{grid-template-columns:1fr}.tlp-form input{height:44px}.tlp-submit{height:47px}}
@media(max-width:420px){.tlp-photo{height:155px;min-height:155px;flex-basis:155px}.tlp-photo img{height:100%;min-height:0}.tlp-photo__label strong{font-size:11px}.tlp-panel{padding:23px 16px 19px}.tlp-copy h2,.tlp-success h2{font-size:25px}.tlp-kicker{min-height:24px;font-size:8px}.tlp-form input{height:42px}.tlp-consent{font-size:8px}}
</style>
